import xmlFieldKeyMapRaw from '../../../assets/rule-config/field-key-map-xml.json';

/**
 * Describes a node-level filter used only in optional `flatFields` overrides (legacy),
 * e.g. `{ node: "Report", attribute: "Version" }` with path `Case/ReportDetails/Report[@Version='1']/…`.
 */
export interface XmlPathNodePredicate {
  node: string;
  attribute: string;
  label?: string;
  dataType?: 'string' | 'number';
}

/** One flattened field: UI map key → how to resolve / persist the XML path. */
export interface XmlFieldMapMetaValue {
  path: string;
  /** Shown in Field input (JSON attr `label`); map key may include `values[]` cartesian prefix. */
  displayKey?: string;
  parentKey?: string;
  isRepeatable?: boolean | string;
  repeatableNodes?: string[];
  nodePredicates?: XmlPathNodePredicate[];
  repeatBy?: { attribute?: string; value?: string | number | boolean };
}

export type XmlFlattenedFieldMapValue = string | XmlFieldMapMetaValue;

/** One entry in attribute/node `values[]` — `value` for xpath, `label` for field key segment. */
interface XmlValueOption {
  value: string;
  label: string;
}

interface XmlAttr {
  name: string;
  label: string;
  type?: string;
  xpath: string;
  dataType?: string;
  /** Plain strings or `{ value, label }` objects (label = map key; value = predicate). */
  values?: string | string[] | Array<string | { value?: string; label?: string }>;
}

interface XmlNode {
  name: string;
  /** Optional UI-only token for map keys (xpath / XML element name stays `name`). */
  label?: string;
  type?: string;
  xpath: string;
  isRepeatable?: boolean;
  /** When false, repeatable wrapper is kept in xpath only (not Build Path / context UI). */
  buildPathStep?: boolean;
  values?: string | string[] | Array<string | { value?: string; label?: string }>;
  discriminateAttribute?: string;
  attributes?: XmlAttr[];
  children?: XmlNode[];
}

interface XmlDoc extends XmlNode {
  flatFields?: Array<{
    label: string;
    xpath: string;
    dataType?: string;
    isRepeatable?: boolean;
    repeatableNodes?: string[];
    nodePredicates?: XmlPathNodePredicate[];
  }>;
}

/**
 * Normalizes an XPath for storage (no leading slash, forward slashes).
 * @example `"/Case/@AccessionNumber"` â†’ `"Case/@AccessionNumber"`
 */
export function normalizeXmlFieldPersistPath(xpath: string): string {
  let s = (xpath || '').trim().replace(/\\/g, '/');
  while (s.startsWith('/')) {
    s = s.slice(1);
  }
  return s;
}

/**
 * Repeatable nodes that need user context (Build Path / occurrence), not bare XML wrappers.
 * `name` stays the element name in xpath; only nodes with filters/label/`buildPathStep` surface in UI.
 */
export function xmlRepeatableNodeExposesInUi(node: XmlNode): boolean {
  if (!node.isRepeatable) {
    return false;
  }
  if (node.buildPathStep === true) {
    return true;
  }
  if (node.buildPathStep === false) {
    return false;
  }
  const hasLabel = !!(node.label || '').trim();
  const hasAttrs = (node.attributes ?? []).length > 0;
  return hasAttrs || hasLabel;
}

/** Repeatable element names along a template path (for UI occurrence / Build Path). */
export function repeatableNodesAlongTemplatePath(doc: unknown, templatePath: string): string[] {
  if (!isTreeSchema(doc)) {
    return [];
  }
  const parts = normalizeXmlFieldPersistPath(templatePath)
    .split('/')
    .filter((p) => p && !p.startsWith('@'));
  let cursor: XmlNode = doc;
  const out: string[] = [];
  let i = 0;
  if (parts[0] === 'Case') {
    i = 1;
  }
  for (; i < parts.length; i++) {
    const bare = parts[i].split('[')[0].trim();
    if (!bare) {
      continue;
    }
    const child = (cursor.children ?? []).find((c) => c.name === bare);
    if (!child) {
      break;
    }
    if (xmlRepeatableNodeExposesInUi(child)) {
      out.push(child.name);
    }
    cursor = child;
  }
  return out;
}

function isTreeSchema(doc: unknown): doc is XmlDoc {
  if (!doc || typeof doc !== 'object') {
    return false;
  }
  const d = doc as XmlDoc;
  return (
    typeof d.name === 'string' &&
    typeof d.xpath === 'string' &&
    (!!d.children?.length || !!d.attributes?.length || !!d.flatFields?.length)
  );
}

/**
 * De-duplicates predicate specs by `node:attribute` (used for optional `flatFields` only).
 * @example `[{node:"Report",attribute:"Version"},{node:"Report",attribute:"Version"}]` â†’ one row.
 */
function dedupePredicates(pred: XmlPathNodePredicate[]): XmlPathNodePredicate[] {
  const seen = new Set<string>();
  return pred.filter((p) => {
    const k = `${p.node}:${p.attribute}`;
    if (seen.has(k)) {
      return false;
    }
    seen.add(k);
    return true;
  });
}

/**
 * Reads `values` from JSON: strings, `{ value, label }`, or comma-separated string.
 * `value` is used in xpath predicates; `label` (when set) is used in cartesian field keys.
 */
function parseValueOptions(raw: unknown): XmlValueOption[] | undefined {
  if (Array.isArray(raw)) {
    const out: XmlValueOption[] = [];
    for (const item of raw) {
      if (item == null) {
        continue;
      }
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        const v = `${item}`.trim();
        if (v) {
          out.push({ value: v, label: v });
        }
        continue;
      }
      if (typeof item === 'object') {
        const o = item as { value?: unknown; label?: unknown };
        const v = `${o.value ?? ''}`.trim();
        if (!v) {
          continue;
        }
        const lbl = `${o.label ?? v}`.trim();
        out.push({ value: v, label: lbl || v });
      }
    }
    return out.length ? out : undefined;
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((v) => ({ value: v, label: v }));
  }
  return undefined;
}

function xmlPredicateAttrFromAttrXPath(xpath: string, jsonAttrName: string): string {
  const m = /@([^/]+)$/.exec((xpath || '').replace(/\\/g, '/').trim());
  return (m?.[1] || jsonAttrName).trim();
}

/**
 * One repeatable dimension: fixed `values[]` for one element + one XML attribute (predicate).
 * @example `ObservationDetails` + `@SubType` + `["Grossing","Diagnosis"]`.
 */
interface RepeatableExpansionConfig {
  element: string;
  /** Used in XPath as `[@predicateAttribute='value']` (often matches XML casing, e.g. `name`). */
  predicateAttribute: string;
  /** JSON attribute `name` — used to skip emitting discriminator rows. */
  sourceAttrName: string;
  options: XmlValueOption[];
}

/** One chosen variant when expanding: `ObservationDetails` + `SubType` + `Grossing`. */
interface Binding {
  element: string;
  predicateAttribute: string;
  /** Predicate literal in xpath (`value` from JSON). */
  value: string;
  /** Segment in field map key (`label` from JSON `values[]`, else `value`). */
  keyPart: string;
}

/**
 * Collects **all** `values[]` dimensions on this repeatable (node-level and/or multiple attrs),
 * e.g. `Type` + `SubType` on `ObservationDetails`, or `Type` on `ServiceProvider`, or `name` on `Observation`.
 * @example `ObservationDetails` with `Type` and `SubType` attrs each with `values` â†’ two configs (Cartesian product later).
 */
function getRepeatableExpansions(node: XmlNode): RepeatableExpansionConfig[] {
  if (!node.isRepeatable) {
    return [];
  }
  const out: RepeatableExpansionConfig[] = [];

  const nodeVals = parseValueOptions(node.values);
  const disc = (node.discriminateAttribute || '').trim();
  if (nodeVals?.length) {
    const pred =
      disc || xmlPredicateAttrFromAttrXPath(node.attributes?.[0]?.xpath ?? '', node.attributes?.[0]?.name ?? 'Type');
    out.push({
      element: node.name,
      predicateAttribute: pred,
      sourceAttrName: disc || (node.attributes?.[0]?.name ?? 'Type'),
      options: nodeVals,
    });
  }

  for (const a of node.attributes ?? []) {
    const av = parseValueOptions(a.values);
    if (!av?.length) {
      continue;
    }
    out.push({
      element: node.name,
      predicateAttribute: xmlPredicateAttrFromAttrXPath(a.xpath, a.name),
      sourceAttrName: a.name,
      options: av,
    });
  }

  const seen = new Set<string>();
  return out.filter((e) => {
    const k = `${e.element}:${e.sourceAttrName}`;
    if (seen.has(k)) {
      return false;
    }
    seen.add(k);
    return true;
  });
}

/** Escapes single quotes inside predicate values for XPath literals. @example `O'Brien` â†’ `O\'Brien`. */
function escapeXmlPredicateValue(s: string): string {
  return `${s}`.replace(/'/g, "\\'");
}

/** Escapes regex metacharacters when matching element names in template paths. */
function escapeReLite(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Applies all bindings: groups by `element`, merges `[@attr='v']` for the same element,
 * replaces `/Element` left-to-right in document order (outer â†’ inner).
 * @example
 *   template: `Case/.../ObservationDetails/Observation/@Value`
 *   row: `[{element:"ObservationDetails",predicateAttribute:"SubType",value:"Grossing"},{element:"ObservationDetails",predicateAttribute:"Type",value:"TC"},{element:"Observation",predicateAttribute:"name",value:"ReportToCancerRegistry"}]`
 *   â†’ `.../ObservationDetails[@SubType='Grossing'][@Type='TC']/Observation[@name='ReportToCancerRegistry']/@Value`
 *   (attribute order within one element follows row order; same-element bindings are merged.)
 */
function injectBindingsFromRow(templatePath: string, bindingRow: Binding[]): string {
  let out = normalizeXmlFieldPersistPath(templatePath);
  const byEl = new Map<string, Binding[]>();
  for (const b of bindingRow) {
    const el = b.element.trim();
    const list = byEl.get(el) || [];
    list.push(b);
    byEl.set(el, list);
  }
  const elements = [...byEl.keys()].sort((a, b) => {
    const ia = out.indexOf(`/${a}/`);
    const ib = out.indexOf(`/${b}/`);
    return (ia === -1 ? 99999 : ia) - (ib === -1 ? 99999 : ib);
  });
  for (const el of elements) {
    const group = byEl.get(el)!;
    const preds = group
      .map((b) => `[@${b.predicateAttribute.trim()}='${escapeXmlPredicateValue(b.value)}']`)
      .join('');
    const seg = `${el}${preds}`;
    const re = new RegExp(`(^|/)${escapeReLite(el)}(?=/|$)`);
    out = out.replace(re, (_m, p1: string) => `${p1}${seg}`);
  }
  return normalizeXmlFieldPersistPath(out);
}

/**
 * Cartesian product of expansion dimensions (outermost config first in `stack`).
 * @example `Type` [TC,PC] Ã— `SubType` [Grossing,Diagnosis] â†’ four binding rows.
 */
function cartesianBindings(stack: RepeatableExpansionConfig[]): Binding[][] {
  if (!stack.length) {
    return [[]];
  }
  const [head, ...tail] = stack;
  const rest = cartesianBindings(tail);
  const out: Binding[][] = [];
  for (const opt of head.options) {
    const b: Binding = {
      element: head.element,
      predicateAttribute: head.predicateAttribute,
      value: opt.value,
      keyPart: (opt.label || opt.value).trim(),
    };
    for (const row of rest) {
      out.push([b, ...row]);
    }
  }
  return out;
}

/** Skip emitting map rows for attributes that only define `values[]` variants on this repeatable. */
function isDiscriminatorAttr(attr: XmlAttr, expsHere: RepeatableExpansionConfig[]): boolean {
  return expsHere.some((e) => e.sourceAttrName === attr.name);
}

/** Attribute label for map keys: use `label` from JSON (e.g. Result.Observation.Name). */
function attrDisplayKeyPart(attr: XmlAttr): string {
  return (attr.label || attr.name || '').trim();
}

/**
 * UI field key / dropdown text: always the `label` from JSON (e.g. `Case.Accession Number`).
 * XML `name` is only for xpath; never prepend node `name`/`label` to build the key.
 */
function attributeMapKey(_node: XmlNode, attr: XmlAttr): string {
  return attrDisplayKeyPart(attr);
}

/** Strip `[@pred]` and `[n]` from element segments; keep `@leaf` as-is. */
export function stripPathPredicates(path: string): string {
  return normalizeXmlFieldPersistPath(path)
    .split('/')
    .map((seg) => {
      if (seg.startsWith('@')) {
        return seg;
      }
      return seg.split('[')[0].trim();
    })
    .join('/');
}

function parsePredicateElementSegmentLocal(token: string): {
  name: string;
  predicates: { attribute: string; value: string }[];
} {
  const t = token.trim();
  let i = 0;
  while (i < t.length && t[i] !== '[') {
    i++;
  }
  const name = t.slice(0, i).trim();
  let rest = t.slice(i);
  const predicates: { attribute: string; value: string }[] = [];
  const re = /^\[@([^\]=\s]+)\s*=\s*(?:'([^']*)'|"([^"]*)"|(\s*[^\]]+))\]/;
  while (true) {
    const m = re.exec(rest);
    if (!m) {
      break;
    }
    const attribute = (m[1] || '').trim();
    const rawVal = (m[2] ?? m[3] ?? (m[4] ?? '').trim()) || '';
    const value = `${rawVal}`.trim().replace(/\\'/g, "'");
    predicates.push({ attribute, value });
    rest = rest.slice(m[0].length);
  }
  return { name, predicates };
}

function attrNameMatchesPredicate(predAttr: string, attr: XmlAttr): boolean {
  const pa = (predAttr || '').trim().toLowerCase();
  if (!pa) {
    return false;
  }
  if (pa === (attr.name || '').trim().toLowerCase()) {
    return true;
  }
  return pa === xmlPredicateAttrFromAttrXPath(attr.xpath, attr.name).toLowerCase();
}

/** Label from `values[]` when `predicateValue` matches a configured `value` (not free-text filters). */
function configuredValueLabel(attr: XmlAttr, predicateValue: string): string | null {
  const opts = parseValueOptions(attr.values);
  if (!opts?.length) {
    return null;
  }
  const v = (predicateValue ?? '').toString().trim();
  const hit = opts.find((o) => o.value === v);
  return hit ? hit.label : null;
}

function walkAllAttributes(node: XmlNode, visit: (owner: XmlNode, attr: XmlAttr) => void): void {
  for (const attr of node.attributes ?? []) {
    visit(node, attr);
  }
  for (const ch of node.children ?? []) {
    walkAllAttributes(ch, visit);
  }
}

/**
 * Resolve display field key from a stored XML path:
 * 1. Strip predicates → match attribute `xpath` → use JSON `label`.
 * 2. For each predicate whose value is listed in that attribute's `values[]`, prefix that entry's `label`.
 */
export function resolveXmlFieldDisplayKeyFromPersistedPath(
  doc: unknown,
  persistedPath: string
): string | null {
  if (!isTreeSchema(doc)) {
    return null;
  }
  const norm = normalizeXmlFieldPersistPath(persistedPath);
  if (!norm) {
    return null;
  }
  const stripped = stripPathPredicates(norm);
  let leafAttr: XmlAttr | null = null;
  let leafNode: XmlNode | null = null;
  walkAllAttributes(doc, (owner, attr) => {
    if (stripPathPredicates(attr.xpath) === stripped) {
      leafAttr = attr;
      leafNode = owner;
    }
  });
  if (!leafAttr || !leafNode) {
    return null;
  }
  const baseLabel = attributeMapKey(leafNode, leafAttr);

  const parts = norm.split('/').filter(Boolean);
  const elementParts = parts.filter((p) => !p.startsWith('@'));
  const prefixes: string[] = [];
  let cursor: XmlNode = doc;
  let i = 0;
  if (elementParts[0] === (doc.name || 'Case')) {
    i = 1;
  }
  for (; i < elementParts.length; i++) {
    const parsed = parsePredicateElementSegmentLocal(elementParts[i]);
    const child = (cursor.children ?? []).find((c) => c.name === parsed.name);
    if (!child) {
      break;
    }
    for (const pred of parsed.predicates) {
      let matched = false;
      for (const a of child.attributes ?? []) {
        if (!attrNameMatchesPredicate(pred.attribute, a)) {
          continue;
        }
        const prefixLabel = configuredValueLabel(a, pred.value);
        if (prefixLabel) {
          prefixes.push(prefixLabel);
          matched = true;
          break;
        }
      }
      if (!matched) {
        /* Free-text filter (e.g. type=22) — not in values[]; do not change field name. */
      }
    }
    cursor = child;
  }

  if (!prefixes.length) {
    return baseLabel;
  }
  return `${prefixes.join('.')}.${baseLabel}`;
}

/** Field key when `values[]` expansions apply: cartesian `keyPart`s + leaf attribute `label`. */
function fieldKeyFromBindings(bindingRow: Binding[], leafAttrLabel: string): string {
  const tail = (leafAttrLabel || '').trim();
  const prefix = bindingRow.map((b) => (b.keyPart || b.value || '').trim()).filter(Boolean);
  if (!prefix.length) {
    return tail;
  }
  return tail ? [...prefix, tail].join('.') : prefix.join('.');
}

function mergeKeyedField(
  map: Record<string, XmlFlattenedFieldMapValue>,
  label: string,
  xpath: string,
  isRepeatable: boolean,
  disambiguator?: string,
  repeatableNodes?: string[],
  nodePredicates?: XmlPathNodePredicate[],
  displayKey?: string
): void {
  const base = (label || '').trim();
  const xp = (xpath || '').trim();
  if (!base || !xp) {
    return;
  }
  let key = base;
  if (map[key]) {
    const suf = (disambiguator || '').trim() || normalizeXmlFieldPersistPath(xp).split('/').pop() || 'field';
    key = `${base} (${suf})`;
  }
  const nodes = repeatableNodes?.length ? [...repeatableNodes] : undefined;
  const preds = dedupePredicates(nodePredicates ?? []).filter((p) => p.node && p.attribute);
  const dk = (displayKey || '').trim();
  map[key] = {
    path: normalizeXmlFieldPersistPath(xp),
    isRepeatable,
    ...(dk && dk !== key ? { displayKey: dk } : {}),
    ...(nodes?.length ? { repeatableNodes: nodes } : {}),
    ...(preds.length ? { nodePredicates: preds } : {}),
  };
}

/**
 * Walks the Case tree and fills `map`:
 *
 * 1. **Plain attributes** — key = JSON `label` only (e.g. `Case.Accession Number`), path from `xpath`.
 * 2. **Only when `values[]` exists** — Cartesian keys use each entry's `label` + leaf attr `label`;
 *    xpath predicates use each entry's `value` (see `fieldKeyFromBindings` / `injectBindingsFromRow`).
 * 3. **Repeatable without `values[]`** — key = JSON `label`, `repeatableNodes` uses XML element `name`.
 */
function walkFlatten(
  map: Record<string, XmlFlattenedFieldMapValue>,
  node: XmlNode,
  expansionStack: RepeatableExpansionConfig[],
  ordinalStack: string[]
): void {
  const expsHere = getRepeatableExpansions(node);

  const legacyOrdinals =
    node.isRepeatable && !expsHere.length ? [...ordinalStack, node.name] : ordinalStack.slice();
  const legacyNeed = legacyOrdinals.length > 0;

  const expansionStackForChildren = expsHere.length ? [...expansionStack, ...expsHere] : expansionStack;

  for (const attr of node.attributes ?? []) {
    const lbl = attrDisplayKeyPart(attr);
    const xp = (attr.xpath || '').trim();
    if (!lbl || !xp) {
      continue;
    }
    if (expsHere.length && isDiscriminatorAttr(attr, expsHere)) {
      if (expansionStack.length) {
        const rows = cartesianBindings(expansionStack);
        for (const bindingRow of rows) {
          const key = fieldKeyFromBindings(bindingRow, lbl);
          const concrete = injectBindingsFromRow(xp, bindingRow);
          const repeatables = repeatableNodesAlongTemplatePath(xmlFieldKeyMapRaw, concrete);
          mergeKeyedField(
            map,
            key,
            concrete,
            repeatables.length > 0,
            attr.name,
            repeatables.length ? repeatables : undefined,
            undefined,
            lbl
          );
        }
      }
      continue;
    }

    if (expansionStack.length) {
      const rows = cartesianBindings(expansionStack);
      for (const bindingRow of rows) {
        const key = fieldKeyFromBindings(bindingRow, lbl);
        const concrete = injectBindingsFromRow(xp, bindingRow);
        const repeatables = repeatableNodesAlongTemplatePath(xmlFieldKeyMapRaw, concrete);
        mergeKeyedField(
          map,
          key,
          concrete,
          repeatables.length > 0,
          attr.name,
          repeatables.length ? repeatables : undefined,
          undefined,
          lbl
        );
      }
      continue;
    }

    if (!node.isRepeatable) {
      const key = attributeMapKey(node, attr);
      mergeKeyedField(map, key, xp, false, attr.name, undefined, undefined);
      continue;
    }

    mergeKeyedField(
      map,
      attributeMapKey(node, attr),
      xp,
      legacyNeed,
      attr.name,
      legacyOrdinals.length ? legacyOrdinals : undefined,
      undefined
    );
  }

  const childOrdinals = node.isRepeatable && !expsHere.length ? [...ordinalStack, node.name] : ordinalStack.slice();
  for (const ch of node.children ?? []) {
    walkFlatten(map, ch, expansionStackForChildren, childOrdinals);
  }
}

/**
 * Converts `field-key-map-xml.json` (tree + optional `flatFields`) into `key â†’ path | meta` for routing.
 * Non-tree JSON objects are passed through as legacy flat maps.
 */
export function flattenXmlFieldKeyMapDocument(doc: unknown): Record<string, XmlFlattenedFieldMapValue> {
  if (!doc || typeof doc !== 'object') {
    return {};
  }
  if (!isTreeSchema(doc)) {
    const out: Record<string, XmlFlattenedFieldMapValue> = {};
    for (const [k, v] of Object.entries(doc as Record<string, unknown>)) {
      if (typeof v === 'string') {
        out[k] = normalizeXmlFieldPersistPath(v);
      } else if (v && typeof v === 'object' && typeof (v as XmlFieldMapMetaValue).path === 'string') {
        const m = v as XmlFieldMapMetaValue;
        out[k] = { ...m, path: normalizeXmlFieldPersistPath(m.path) };
      }
    }
    return out;
  }
  const out: Record<string, XmlFlattenedFieldMapValue> = {};
  walkFlatten(out, doc, [], []);
  for (const ff of doc.flatFields || []) {
    const lbl = (ff.label || '').trim();
    const xp = (ff.xpath || '').trim();
    if (!lbl || !xp) {
      continue;
    }
    const normalizedXp = normalizeXmlFieldPersistPath(xp);
    let rn = (ff.repeatableNodes ?? []).map((s) => `${s}`.trim()).filter(Boolean);
    const np = (ff.nodePredicates ?? [])
      .map((p) => ({
        node: `${(p as XmlPathNodePredicate).node ?? ''}`.trim(),
        attribute: `${(p as XmlPathNodePredicate).attribute ?? ''}`.trim(),
        label: (p as XmlPathNodePredicate).label,
        dataType: (p as XmlPathNodePredicate).dataType,
      }))
      .filter((p) => p.node && p.attribute);
    const existing = out[lbl];
    if (
      ff.isRepeatable &&
      !rn.length &&
      existing &&
      typeof existing === 'object' &&
      typeof (existing as XmlFieldMapMetaValue).path === 'string'
    ) {
      const prev = existing as XmlFieldMapMetaValue;
      if (
        normalizeXmlFieldPersistPath(prev.path) === normalizedXp &&
        (prev.repeatableNodes?.length ?? 0) > 0
      ) {
        rn = [...(prev.repeatableNodes ?? [])];
      }
    }
    out[lbl] = {
      path: normalizedXp,
      isRepeatable: !!ff.isRepeatable,
      ...(rn.length ? { repeatableNodes: rn } : {}),
      ...(np.length ? { nodePredicates: np } : {}),
    };
  }
  return out;
}

let flattenedXmlFieldKeyMapCache: Record<string, XmlFlattenedFieldMapValue> | null = null;

export function getFlattenedXmlFieldKeyMap(): Record<string, XmlFlattenedFieldMapValue> {
  if (!flattenedXmlFieldKeyMapCache) {
    flattenedXmlFieldKeyMapCache = flattenXmlFieldKeyMapDocument(xmlFieldKeyMapRaw);
  }
  return flattenedXmlFieldKeyMapCache;
}

/** Lazy singleton — do not run flatten at module import time. */
export const flattenedXmlFieldKeyMap: Record<string, XmlFlattenedFieldMapValue> = new Proxy(
  {} as Record<string, XmlFlattenedFieldMapValue>,
  {
    get(_t, prop) {
      const map = getFlattenedXmlFieldKeyMap();
      if (prop === Symbol.toStringTag) {
        return 'Object';
      }
      if (prop === 'then') {
        return undefined;
      }
      return Reflect.get(map, prop);
    },
    ownKeys() {
      return Reflect.ownKeys(getFlattenedXmlFieldKeyMap());
    },
    getOwnPropertyDescriptor(_t, prop) {
      return Reflect.getOwnPropertyDescriptor(getFlattenedXmlFieldKeyMap(), prop);
    },
  }
);
